import { memo, useCallback, useMemo } from 'react'
import { Icon, IconButton, Li, Link, Typography } from '@vp/swan'
import type { NavigationCategory } from '../../../../types/navigation'
import { Badge } from '../../../common/Badge'
import { getHolidayThemeStyles } from '../../../../utils/navigation'
import { getHeaderTracker } from '../../../../tracking'
import { TOP_NAV_LEVEL, TOP_NAV } from '../../../../constants/analytics'
import { useHoverIntent } from '../../../../hooks/useHoverIntent'
import { desktopNavItem, desktopNavItemInner } from './DesktopNavigationItem.scss'
import { DesktopNavigationL2 } from './DesktopNavigationL2'
import { useI18n } from '../../../../context/TranslationContext'
import { useNavContext } from '../../../../context/NavContextProvider'

interface DesktopNavigationItemProps {
  item: NavigationCategory
  isActive: boolean
  updateActiveTab: (id: string) => void
  onClose: () => void
}

/**
 * A single top-level desktop navigation item.
 * Handles hover intent, keyboard interaction, tracking, and ARIA attributes.
 */
export const DesktopNavigationItem: React.FC<DesktopNavigationItemProps> = memo(({ item, isActive, updateActiveTab, onClose }) => {
  const { translate } = useI18n()
  const hasChildren = Boolean(item.children?.length)
  const holidayThemeStyle = useMemo(() => getHolidayThemeStyles(item.theme), [item.theme])
  const { hoverNavItem } = useNavContext()

  const hoverIn = useCallback(() => {
    hoverNavItem(item.id, false)
    updateActiveTab(item.id)
  }, [item.id, updateActiveTab])
  const itemRef = useHoverIntent<HTMLDivElement>({ hoverIn })

  const trackNavItem = useCallback(() => {
    getHeaderTracker().trackLink({
      subSection: TOP_NAV,
      navigationDetailPath: [item.analyticsId || item.text],
      navigationSection: TOP_NAV_LEVEL,
      href: item.url,
    })
  }, [item.analyticsId, item.text, item.url])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      // For leaf items, Space should activate the link (WAI-ARIA menuitem pattern).
      // Browsers only handle Enter natively on <a> elements, not Space.
      if (!hasChildren) {
        if (event.key === ' ') {
          event.preventDefault()
          event.currentTarget.click()
        }
        return
      }

      const trigger = event.currentTarget
      if (['Enter', ' '].includes(event.key)) {
        event.preventDefault()
        event.stopPropagation()
        updateActiveTab(item.id)
        trackNavItem()
        trigger.dataset['keyboardNav'] = ''
      }

      if (['Escape', 'Tab'].includes(event.key)) {
        onClose()
        delete trigger.dataset['keyboardNav']
      }
    },
    [item.id, onClose, updateActiveTab, trackNavItem, hasChildren],
  )

  return (
    <Li role="none" className={desktopNavItem}>
      <div className={desktopNavItemInner}>
        <Link
          ref={itemRef}
          role="menuitem"
          onClick={trackNavItem}
          onFocus={() => hoverNavItem(item.id)}
          py={5}
          px={3}
          href={item.url}
          skin="unstyled"
          style={{ justifyContent: 'center' }}
          {...(hasChildren && {
            'aria-haspopup': 'menu' as const,
            'aria-expanded': isActive,
            'aria-controls': `${item.id}-submenu`,
          })}
        >
          <Typography as="p" fontSkin="body-small" textColor="standard" textAlign="center" m={0}>
            <span style={holidayThemeStyle}>{item.text}</span>
            {item.badge && (
              <>
                &nbsp;
                <Badge badge={item.badge} />
              </>
            )}
          </Typography>
        </Link>
        {hasChildren && (
          <IconButton
            skin="tertiary"
            tooltip={translate('menu.showSub')}
            onKeyDown={handleKeyDown}
            aria-label={translate('menu.showSub')}
            aria-expanded={isActive}
            aria-controls={`${item.id}-submenu`}
            id={`${item.id}-button`}
            aria-haspopup="menu"
            py={0}
          >
            <Icon iconType="caretDown" />
          </IconButton>
        )}
      </div>
      {hasChildren && <DesktopNavigationL2 key={item.id} data={item} onClose={onClose} isActive={isActive} />}
    </Li>
  )
})
